<script lang="ts">
  import { onDestroy } from 'svelte'

  import SubmitButton from './Submit.svelte'
  import SlackIcon from './icons/Slack.svelte'
  import DiscordIcon from './icons/Discord.svelte'
  import { Providers, login, logout, getMostRecentLogin } from '../utils/auth'
  import { getWeekends, getUserVotes, postUserVotes } from '../utils/db'
  import { user } from '../utils/store'

  const loadingGifUrl = 'img/party_parrot.gif'
  let errorMessage = ''
  let submitting = false
  let hasSubmitted = false
  let hasVotedBefore = false
  let hasFetched = false
  let fetching = false
  let provider: Providers | null

  let votes = {}
  let weekends = []

  const fetchData = async () => {
    if (fetching) return
    fetching = true
    errorMessage = ''

    const weekendResponse = await getWeekends()
    if (weekendResponse.error) {
      // todo: display error to user
      console.log(weekendResponse.error)
    } else {
      weekends = weekendResponse.data
    }

    const userVoteResponse = await getUserVotes($user.id)
    if (userVoteResponse.error) {
      // todo: display error to user
      console.log(userVoteResponse.error)
    } else {
      const userVotes = userVoteResponse.data
      const userWeekendIds = userVotes.length && userVotes[0].weekend_ids ? userVotes[0].weekend_ids : []
      // set initial vote selections based on prior user response
      votes = weekends.reduce((acc, weekend) => {
        acc[weekend.id] = userWeekendIds.indexOf(weekend.id) > -1
        return acc
      }, {})
      hasVotedBefore = !!userVotes.length
    }

    fetching = false
    hasFetched = true
  }

  const unsub = user.subscribe(() => {
    if (!$user) return
    if (!hasFetched) {
      fetchData()
    }
    provider = getMostRecentLogin($user.identities)
  })
  onDestroy(unsub)

  const handleLoginSlack = () => login(Providers.slack)
  const handleLoginDiscord = () => login(Providers.discord)
  const handleLogout = () => {
    hasFetched = false
    logout()
  }

  const handleSubmit = async () => {
    errorMessage = ''
    submitting = true
    hasSubmitted = false

    const weekendIds = Object.keys(votes).reduce((acc, v) => {
      if (votes[v]) {
        acc.push(v)
      }
      return acc
    }, [])

    const { error } = await postUserVotes($user.id, weekendIds)
    if (error) {
      console.log(error)
      errorMessage = error.message
      hasVotedBefore = false
    } else {
      hasVotedBefore = true
      hasSubmitted = true
    }

    submitting = false
  }

  function handleChange(e) {
    votes[e.target.id] = e.target.checked
  }
</script>

<svelte:head>
  <link rel="preload" as="image" href={loadingGifUrl} />
</svelte:head>

{#if !$user}
  <p>but first you gotta login to pick em:</p>
  <div class="login-providers">
    <button class="login slack" on:click={handleLoginSlack}>
      <SlackIcon />
      <span>Slack</span>
    </button>
    <p>or</p>
    <button class="login discord" on:click={handleLoginDiscord}>
      <DiscordIcon />
      <span>Discord</span>
    </button>
  </div>
{:else if fetching}
  <p><img src={loadingGifUrl} alt="loading" width="30px" height="30px" /> loading dates...</p>
{:else}
  {#if errorMessage}
    <p>there was an error updating your votes</p>
    <p>try again or yell at andrew, or both</p>
    <p>message: {errorMessage}</p>
  {/if}
  <form class="poll" on:submit|preventDefault={handleSubmit}>
    <div class="choices">
      {#each weekends as w}
        <div class="choice">
          <input
            type="checkbox"
            id={w.id}
            name={w.weekend}
            value={w.id}
            on:change={handleChange}
            bind:checked={votes[w.id]}
          />
          <label for={w.id}>{w.weekend.toLowerCase()}</label>
        </div>
      {/each}
    </div>
    <div class="submit">
      <SubmitButton hasVotedBefore={hasVotedBefore} submitting={submitting} />
      {#if hasSubmitted}
        <div class="thanks">
          {#if hasVotedBefore}
            <p>thanks for updating your vote!</p>
          {:else}
            <p>thanks for submitting your vote!</p>
            <p>you can updated it at any time</p>
          {/if}
        </div>
      {/if}
    </div>
  </form>
  <div class="logout">
    {#if provider === Providers.slack}
      <button class="login slack" on:click={handleLogout}>
        <SlackIcon />
        <span>logout</span>
      </button>
    {:else if provider === Providers.discord}
      <button class="login discord" on:click={handleLogout}>
        <DiscordIcon />
        <span>logout</span>
      </button>
    {/if}
  </div>
{/if}

<style>
  .choices {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    justify-content: space-between;
  }
  .choice {
    display: flex;
    align-items: center;
    margin: 0.5rem 0;
  }
  .choice:last-child {
    margin-bottom: 0;
  }
  .choice label {
    margin-left: 0.5rem;
  }
  input[type="checkbox"] {
    margin: 0;
  }
  .login {
    display: flex;
    align-items: center;
    margin: 0;
    padding: 0.5rem 1rem;
    border: none;
  }
  .login-providers {
    display: flex;
    flex-direction: row;
    justify-content: space-around;
  }
  .login-providers p {
    margin: 0;
    display: flex;
    align-items: center;
  }
	@media only screen and (max-width: 600px) {
    .login-providers {
      flex-direction: column;
      align-items: center;
    }
    .login-providers p {
      margin: 1rem;
    }
  }
  .login:hover {
    cursor: pointer;
  }
  .login span {
    margin-left: 1rem;
  }
  .slack {
    background-color: #4b154b;
    color: var(--color);
  }
  .discord {
    background-color: #5865F2;
    color: var(--color);
  }
  .submit {
    display: flex;
    margin-top: 2rem;
  }
  .thanks {
    margin-left: 1rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  .thanks p {
    margin: 0 0 0.5rem;
  }
  .thanks p:last-child {
    margin-bottom: 0;
  }
  .logout {
    margin-top: 2rem;
  }
</style>
